import { useState } from "react";
import { useLocation, Link } from "react-router-dom";
import { ArrowRight, Check, Loader2 } from "lucide-react";
import { captureLead } from "@/lib/api";

type Status = "idle" | "loading" | "success" | "error";

export default function LeadCaptureBox() {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<Status>("idle");
  const location = useLocation();

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!email.trim() || status === "loading") return;

    setStatus("loading");
    try {
      await captureLead(email.trim(), location.pathname);
      setStatus("success");
    } catch {
      setStatus("error");
      setTimeout(() => setStatus("idle"), 3000);
    }
  }

  if (status === "success") {
    return (
      <div className="flex items-center gap-3 px-6 py-4 bg-green-50 rounded-full border border-green-200">
        <div className="w-6 h-6 rounded-full bg-green-500 flex items-center justify-center flex-shrink-0">
          <Check size={14} className="text-white" strokeWidth={3} />
        </div>
        <span className="text-sm font-semibold text-green-800">
          You're in! We'll be in touch soon.
        </span>
      </div>
    );
  }

  return (
    <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 w-full max-w-xl">
      <div className="relative flex-1">
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && handleSubmit(e)}
          placeholder="Enter your work email"
          className="w-full px-5 py-3.5 rounded-full bg-white border border-black/[0.08] text-sm text-ink placeholder:text-slate/50 focus:outline-none focus:border-coral focus:ring-2 focus:ring-coral/20 transition-all shadow-sm"
          disabled={status === "loading"}
        />
      </div>
      <button
        onClick={handleSubmit}
        disabled={status === "loading" || !email.trim()}
        className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-coral text-white text-sm font-semibold hover:bg-coral-dark transition-all shadow-md shadow-coral/25 disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0"
      >
        {status === "loading" ? (
          <Loader2 size={14} className="animate-spin" />
        ) : (
          <>
            Start Free <ArrowRight size={14} />
          </>
        )}
      </button>
      {status === "error" && (
        <p className="text-xs text-red-500 sm:absolute sm:-bottom-6 sm:left-0">
          Something went wrong. Try again.
        </p>
      )}
    </div>
  );
}
